// The Cloudish mark: the geometry, the stars it's built from, and the mark
// itself in color, white, and black. Everything that shows the mark draws
// from here: the site's Logo (header, footer, sidebar), /logo's brand kit
// and diagrams, /socials, and scripts/export-brand.mjs (favicon, app icon,
// the intro film's lockups), so none of them can drift from each other.
//
// The outline is one probe's trajectory: clockwise arcs around four stars
// (τ Ceti, the Sun, α Centauri A, α Centauri B), joined by small
// counter-clockwise arcs around three "moons" so the bumps flow into each
// other, closed by a flat base. Two comets ride it: Luyten's Star heading
// out, Proxima Centauri coming home. Every star is drawn at its true radius
// relative to the others (α Centauri A = 12 units); see STARS for sources.

import { createContext, useContext, useId, type ReactNode } from 'react';

export const C = {
  amber: '#f0a828',
  amberLight: '#ffcf6e',
  amberDeep: '#c07f06',
  indigo: '#5457e5',
  indigoLight: '#7577f0',
  ink: '#0b0c11',
};

// ------------------------------------------------------------------ stars

export interface Star {
  key: 'tauCeti' | 'sun' | 'alphaA' | 'alphaB' | 'luyten' | 'proxima';
  name: string;
  /** Radius in solar radii. */
  radius: number;
  /** Distance from the Sun in light-years. */
  distance: number;
  spectral: string;
  role: string;
  source: string;
}

// Radii and distances as published on each star's Wikipedia page
// (September 2026), which cite the underlying measurements.
export const STARS: Record<Star['key'], Star> = {
  tauCeti: {
    key: 'tauCeti',
    name: 'τ Ceti',
    radius: 0.793,
    distance: 11.912,
    spectral: 'G8V',
    role: 'The left bump, on the base',
    source: 'https://en.wikipedia.org/wiki/Tau_Ceti',
  },
  sun: {
    key: 'sun',
    name: 'The Sun',
    radius: 1,
    distance: 0,
    spectral: 'G2V',
    role: 'The middle bump',
    source: 'https://en.wikipedia.org/wiki/Sun',
  },
  alphaA: {
    key: 'alphaA',
    name: 'α Centauri A',
    radius: 1.223,
    distance: 4.344,
    spectral: 'G2V',
    role: 'The tallest bump',
    source: 'https://en.wikipedia.org/wiki/Alpha_Centauri',
  },
  alphaB: {
    key: 'alphaB',
    name: 'α Centauri B',
    radius: 0.864,
    distance: 4.344,
    spectral: 'K1V',
    role: 'The right bump, on the base',
    source: 'https://en.wikipedia.org/wiki/Alpha_Centauri',
  },
  luyten: {
    key: 'luyten',
    name: "Luyten's Star",
    radius: 0.293,
    distance: 12.348,
    spectral: 'M3.5V',
    role: 'The amber comet, heading out',
    source: 'https://en.wikipedia.org/wiki/Luyten%27s_Star',
  },
  proxima: {
    key: 'proxima',
    name: 'Proxima Centauri',
    radius: 0.1542,
    distance: 4.2465,
    spectral: 'M5.5Ve',
    role: 'The indigo comet, coming home',
    source: 'https://en.wikipedia.org/wiki/Proxima_Centauri',
  },
};

/** Jupiter, for scale only (not in the mark): 69,911 km over the Sun's
 *  nominal 695,700 km. Proxima is only about 1.5 times its size. */
export const JUPITER_RADIUS = 69911 / 695700;

/** Units per solar radius: α Centauri A, the largest star, is 12 units. */
export const K = 12 / STARS.alphaA.radius;

// --------------------------------------------------------------- geometry

export type Circle = [cx: number, cy: number, r: number];
type Arc = { c: [number, number]; r: number; a0: number; a1: number };

/** The upper of the two points where circles a and b cross. */
export function upperCrossing(a: Circle, b: Circle): [number, number] {
  const [x0, y0, r0] = a;
  const [x1, y1, r1] = b;
  const d = Math.hypot(x1 - x0, y1 - y0);
  const l = (r0 * r0 - r1 * r1 + d * d) / (2 * d);
  const h = Math.sqrt(r0 * r0 - l * l);
  const mx = x0 + ((x1 - x0) * l) / d;
  const my = y0 + ((y1 - y0) * l) / d;
  const p1: [number, number] = [mx + ((y1 - y0) * h) / d, my - ((x1 - x0) * h) / d];
  const p2: [number, number] = [mx - ((y1 - y0) * h) / d, my + ((x1 - x0) * h) / d];
  return p1[1] < p2[1] ? p1 : p2;
}

/** A cloud traced as an orbit chain: clockwise arcs around `planets` (left
 *  to right over the top; the first and last dropped to sit tangent to the
 *  base), joined by counter-clockwise arcs around moons of radius
 *  `fillet` (one radius for every join, or one per join), then the flat
 *  base back to the start. `at(t)` walks it, t = 0 at the bottom of the
 *  first planet, increasing clockwise. */
export function orbitChain(planets: Circle[], fillets: number | number[], baseY = 50) {
  const filletAt = (i: number) => (typeof fillets === 'number' ? fillets : fillets[i]);
  const ps: Circle[] = planets.map((c, i) => (i === 0 || i === planets.length - 1 ? [c[0], baseY - c[2], c[2]] : c));
  const moons: [number, number][] = [];
  for (let i = 0; i < ps.length - 1; i++) {
    const a = ps[i];
    const b = ps[i + 1];
    moons.push(upperCrossing([a[0], a[1], a[2] + filletAt(i)], [b[0], b[1], b[2] + filletAt(i)]));
  }
  const touch = (c: Circle, m: [number, number]): [number, number] => {
    const d = Math.hypot(m[0] - c[0], m[1] - c[1]);
    return [c[0] + ((m[0] - c[0]) * c[2]) / d, c[1] + ((m[1] - c[1]) * c[2]) / d];
  };
  const ang = (c: [number, number], p: [number, number]) => Math.atan2(p[1] - c[1], p[0] - c[0]);
  const arcs: Arc[] = [];
  for (let i = 0; i < ps.length; i++) {
    const c = ps[i];
    const start: [number, number] = i === 0 ? [c[0], c[1] + c[2]] : touch(c, moons[i - 1]);
    const end: [number, number] = i === ps.length - 1 ? [c[0], c[1] + c[2]] : touch(c, moons[i]);
    const a0 = ang([c[0], c[1]], start);
    let a1 = ang([c[0], c[1]], end);
    while (a1 <= a0) a1 += Math.PI * 2; // clockwise, around the planet
    arcs.push({ c: [c[0], c[1]], r: c[2], a0, a1 });
    if (i < ps.length - 1) {
      const m = moons[i];
      const b0 = ang(m, touch(c, m));
      let b1 = ang(m, touch(ps[i + 1], m));
      while (b1 >= b0) b1 -= Math.PI * 2; // counter-clockwise, around the moon
      arcs.push({ c: m, r: filletAt(i), a0: b0, a1: b1 });
    }
  }
  const first = ps[0];
  const last = ps[ps.length - 1];
  const lens = [...arcs.map((a) => a.r * Math.abs(a.a1 - a.a0)), Math.abs(last[0] - first[0])];
  const total = lens.reduce((x, y) => x + y, 0);
  // Where each planet's arc starts and ends along the path (0..1): planet
  // arcs are the even-numbered ones, moons sit between them.
  const planetSpans: [t0: number, t1: number][] = [];
  let run = 0;
  arcs.forEach((_, i) => {
    if (i % 2 === 0) planetSpans.push([run / total, (run + lens[i]) / total]);
    run += lens[i];
  });
  const pt = (a: Arc, t: number): [number, number] => {
    const g = a.a0 + (a.a1 - a.a0) * t;
    return [a.c[0] + a.r * Math.cos(g), a.c[1] + a.r * Math.sin(g)];
  };
  const at = (t: number): [number, number] => {
    let rest = (((t % 1) + 1) % 1) * total;
    for (let i = 0; i < arcs.length; i++) {
      if (rest <= lens[i]) return pt(arcs[i], lens[i] ? rest / lens[i] : 0);
      rest -= lens[i];
    }
    const f = rest / lens[lens.length - 1];
    return [last[0] + (first[0] - last[0]) * f, baseY];
  };
  let d = `M ${first[0]} ${baseY}`;
  for (const a of arcs) {
    const [ex, ey] = pt(a, 1);
    const sweep = a.a1 > a.a0 ? 1 : 0;
    const large = Math.abs(a.a1 - a.a0) > Math.PI ? 1 : 0;
    d += ` A ${a.r} ${a.r} 0 ${large} ${sweep} ${ex.toFixed(3)} ${ey.toFixed(3)}`;
  }
  d += ' Z';
  const pts = Array.from({ length: 240 }, (_, k) => at(k / 240));
  const xs = pts.map((q) => q[0]);
  const ys = pts.map((q) => q[1]);
  const bounds = { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
  /** A transform that centers the shape in a 64 box, `width` wide. */
  const fit = (width: number) => {
    const k = width / (bounds.maxX - bounds.minX);
    return `translate(32 32) scale(${k.toFixed(4)}) translate(${(-(bounds.minX + bounds.maxX) / 2).toFixed(3)} ${(-(bounds.minY + bounds.maxY) / 2).toFixed(3)})`;
  };
  return { d, at, total, planets: ps, planetSpans, moons, fillet: filletAt(0), baseY, bounds, fit };
}

export type Chain = ReturnType<typeof orbitChain>;

/** The mark's trajectory: the four stars, left to right, at their true
 *  relative radii; the Sun and α Centauri A as the two middle bumps; three
 *  moons of radius 3 between them. */
export const CHAIN = orbitChain(
  [
    [14, 0, STARS.tauCeti.radius * K],
    [25, 30, STARS.sun.radius * K],
    [38, 24, STARS.alphaA.radius * K],
    [50, 0, STARS.alphaB.radius * K],
  ],
  3,
);

// The top bump, α Centauri A's arc, along the path: the "sun" of the
// mark. It's the only stretch in amber; everything else is the cloud.
const [SUN_T0, SUN_T1] = CHAIN.planetSpans[2];
// Where Proxima's trail ends, on α Centauri B.
const HOME_TAIL_END = 0.63;

/** Proxima's tail: a little narrower than the head from the start (85% of
 *  Proxima's diameter), so the head still reads as the comet's, held at
 *  that width (and fully opaque) for most of the cloud, to 62% of the way
 *  back, then narrowing to 30% and fading out at the end. */
const HOLD_WIDTH = 0.85;
const HOLD = 0.62;
export const HOME_TAIL: TailProfile = (d) => {
  if (d < HOLD) return { width: HOLD_WIDTH, opacity: 1 };
  const f = (d - HOLD) / (1 - HOLD);
  return { width: HOLD_WIDTH - (HOLD_WIDTH - 0.3) * f, opacity: 1 - 0.85 * f };
};

/** The mark's comets: where each head sits along CHAIN (0..1) and how far
 *  back its tail reaches. Luyten's rides exactly the top bump, head at its
 *  end; Proxima's starts where the top bump begins and trails back around
 *  the rest of the cloud to α Centauri B, so the sun is amber and the
 *  cloud indigo. Luyten's tail keeps a 4.2 width (a tail as wide as its
 *  head would swamp the outline); Proxima's is exactly its diameter. */
export const COMETS = {
  out: { head: SUN_T1, span: SUN_T1 - SUN_T0, width: 4.2, headR: STARS.luyten.radius * K, color: C.amber, steps: 72 },
  home: {
    head: SUN_T0,
    span: SUN_T0 + 1 - HOME_TAIL_END,
    width: 2 * STARS.proxima.radius * K,
    headR: STARS.proxima.radius * K,
    color: C.indigoLight,
    steps: 112,
    profile: HOME_TAIL,
  },
};

export const TRACK = { color: C.indigo, opacity: 0.26, width: 4.4 };
export const MARK_WIDTH = 54;

// ----------------------------------------------------------------- render

export function useIds(...names: string[]) {
  const base = useId().replace(/\W/g, '');
  return Object.fromEntries(names.map((n) => [n, `${n}-${base}`])) as Record<string, string>;
}

/** Which version of the mark renders: full color, or a one-color white or
 *  black silhouette (through MonoFilter). */
export type Tone = 'color' | 'white' | 'black';
export const ToneContext = createContext<Tone>('color');

/** One-color version of whatever's inside: every pixel's color becomes
 *  white (or black), keeping its alpha, so faint parts (the track) stay
 *  faint and the mark keeps its anti-aliased edges. */
function MonoFilter({ id, tone }: { id: string; tone: 'white' | 'black' }) {
  const v = tone === 'white' ? 1 : 0;
  return (
    <filter id={id} colorInterpolationFilters="sRGB">
      <feColorMatrix type="matrix" values={`0 0 0 0 ${v}  0 0 0 0 ${v}  0 0 0 0 ${v}  0 0 0 1 0`} />
    </filter>
  );
}

export function Svg({ size, viewBox = '0 0 64 64', children, title }: { size: number | string; viewBox?: string; children: ReactNode; title?: string }) {
  const tone = useContext(ToneContext);
  const id = useIds('mono');
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={viewBox}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      style={{ display: 'block' }}
    >
      {tone === 'color' ? (
        children
      ) : (
        <>
          <defs>
            <MonoFilter id={id.mono} tone={tone} />
          </defs>
          <g filter={`url(#${id.mono})`}>{children}</g>
        </>
      )}
    </svg>
  );
}

/** How a comet's tail is shaped: its width (as a fraction of `width`) and
 *  opacity at `d`, the distance back from the head (0) to the tail's end
 *  (1). */
export type TailProfile = (d: number) => { width: number; opacity: number };

/** The default: a straight taper, full at the head, 35% wide and 15%
 *  opaque at the end. */
export const LINEAR_TAIL: TailProfile = (d) => ({ width: 0.35 + 0.65 * (1 - d), opacity: 0.15 + 0.85 * (1 - d) });

/** The comet: `span` of `path` behind the point at `head`, drawn as
 *  `steps` round-capped segments shaped by `profile`, then the head
 *  itself. The overlapping caps read as a string of fading circles. */
export function Comet({
  path,
  head,
  span,
  width,
  color,
  headR,
  steps = 36,
  profile = LINEAR_TAIL,
}: {
  path: { at: (t: number) => [number, number] };
  head: number;
  span: number;
  width: number;
  color: string;
  headR: number;
  steps?: number;
  profile?: TailProfile;
}) {
  const parts = Array.from({ length: steps }, (_, k) => {
    const t0 = head - span + (span * k) / steps;
    const t1 = head - span + (span * (k + 1)) / steps;
    const { width: w, opacity } = profile(1 - (k + 1) / steps);
    const [x0, y0] = path.at(t0);
    const [x1, y1] = path.at(t1);
    return <line key={k} x1={x0} y1={y0} x2={x1} y2={y1} stroke={color} strokeOpacity={opacity} strokeWidth={width * w} strokeLinecap="round" />;
  });
  const [hx, hy] = path.at(head);
  return (
    <>
      {parts}
      <circle cx={hx} cy={hy} r={headR} fill={color} />
    </>
  );
}

/** The mark's contents in CHAIN's own coordinates, with the comets' heads
 *  optionally moved (the animated diagram does), for composing into other
 *  drawings. */
export function MarkBody({ outHead = COMETS.out.head, homeHead = COMETS.home.head }: { outHead?: number; homeHead?: number }) {
  return (
    <>
      <path d={CHAIN.d} fill="none" stroke={TRACK.color} strokeOpacity={TRACK.opacity} strokeWidth={TRACK.width} />
      <Comet path={CHAIN} {...COMETS.out} head={outHead} />
      <Comet path={CHAIN} {...COMETS.home} head={homeHead} />
    </>
  );
}

/** The mark cropped tight to its own ink (the trajectory, its track, and
 *  the comet heads), for sitting beside text: `width` px wide, as tall as
 *  its shape needs. Renders in the surrounding ToneContext's tone. */
// The mark cropped tight to its own ink (plus the stroke's reach): its
// viewBox, and so its height for a given width.
function markBox() {
  const m = Math.max(TRACK.width / 2, COMETS.out.headR) + 0.4;
  const { minX, maxX, minY, maxY } = CHAIN.bounds;
  return { m, minX, minY, w: maxX - minX + 2 * m, h: maxY - minY + 2 * m };
}

/** How tall MarkArt is at `width` — for sizing it as an image file
 *  (public/cloudish-mark.svg, see components/Logo.tsx). */
export function markHeight(width: number) {
  const { w, h } = markBox();
  return (width * h) / w;
}

export function MarkArt({ width, title }: { width: number; title?: string }) {
  const { m, minX, minY, w, h } = markBox();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={(width * h) / w}
      viewBox={`${(minX - m).toFixed(3)} ${(minY - m).toFixed(3)} ${w.toFixed(3)} ${h.toFixed(3)}`}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      style={{ display: 'block' }}
    >
      <ToneBody>
        <MarkBody />
      </ToneBody>
    </svg>
  );
}

/** Applies the surrounding ToneContext's one-color filter, if any, to its
 *  children (for SVGs that don't go through Svg). */
function ToneBody({ children }: { children: ReactNode }) {
  const tone = useContext(ToneContext);
  const id = useIds('mono');
  if (tone === 'color') return <>{children}</>;
  return (
    <>
      <defs>
        <MonoFilter id={id.mono} tone={tone} />
      </defs>
      <g filter={`url(#${id.mono})`}>{children}</g>
    </>
  );
}

/** The Cloudish mark, square, `size` px. Renders in the surrounding
 *  ToneContext's tone. */
export function CloudishMark({ size, title }: { size: number | string; title?: string }) {
  return (
    <Svg size={size} title={title}>
      <g transform={CHAIN.fit(MARK_WIDTH)}>
        <MarkBody />
      </g>
    </Svg>
  );
}
